<template>
    <div>
        <div v-if="error" class="text-center text-red-600 mb-8">
            {{ error }}
        </div>

        <!-- Hero variant: vehicle-class tabs + duration cards -->
        <template v-if="variant === 'hero'">
            <div v-if="heroCategories.length" class="flex justify-center mb-3">
                <div
                    class="inline-flex flex-wrap justify-center gap-1 rounded-full bg-white/10 border border-white/25 p-1 backdrop-blur-sm"
                    role="tablist"
                    aria-label="Vehicle class"
                >
                    <button
                        v-for="cat in heroCategories"
                        :key="cat.id"
                        type="button"
                        role="tab"
                        :aria-selected="activeCategoryId === cat.id"
                        class="px-5 py-2 rounded-full text-sm font-bold transition-colors"
                        :class="activeCategoryId === cat.id
                            ? 'bg-white text-si-blue-dark'
                            : 'text-white hover:bg-white/15'"
                        @click="activeCategoryId = cat.id"
                    >
                        {{ cat.title }}
                    </button>
                </div>
            </div>

            <p v-if="activeCategoryText" class="text-center text-sm text-white/70 mb-8">
                {{ activeCategoryText }}
            </p>

            <div v-if="loading" class="flex justify-center items-center py-20">
                <div class="spinner"></div>
            </div>
            <div
                v-else-if="heroCategoryProducts.length"
                class="grid gap-6 max-w-6xl mx-auto items-stretch pt-6 pb-4"
                :class="heroPricingGridClass"
            >
                <div
                    v-for="(product, index) in heroCategoryProducts"
                    :key="product.id"
                    class="relative flex flex-col h-full pt-7"
                >
                    <span
                        v-if="isHeroMostPopularCard(product, index)"
                        class="absolute top-2 left-1/2 -translate-x-1/2 z-10 rounded-full bg-buy-vignette text-white text-sm font-extrabold uppercase tracking-wide px-6 py-2 shadow-lg ring-2 ring-white whitespace-nowrap"
                    >
                        {{ getPillLabel(getCardMeta(product)) || texts['duration-most_popular'] || 'Most Popular' }}
                    </span>

                    <div
                        class="group flex flex-col flex-1 h-full bg-white rounded-[1.25rem] shadow-xl items-center text-center border-2 border-gray-100 p-6 transition-all duration-300 ease-out hover:-translate-y-2 hover:shadow-2xl hover:border-gray-200"
                    >
                        <div class="flex flex-col items-center flex-1 w-full min-h-[280px]">
                        <template v-if="getProductThumbnail(product)">
                            <div class="w-20 h-20 rounded-sm overflow-hidden mb-5 flex-shrink-0 flex items-center justify-center bg-gray-50">
                                <img
                                    :src="getProductThumbnail(product)"
                                    :alt="productCardTitle(product)"
                                    class="w-full h-full object-contain"
                                />
                            </div>
                        </template>
                        <div
                            v-else
                            class="w-20 h-20 rounded-xl border-2 flex flex-col overflow-hidden mb-5 flex-shrink-0 bg-white"
                            :class="product.badgeBorderClass"
                        >
                            <div
                                class="w-full py-1 flex items-center justify-center text-white text-[10px] font-black uppercase tracking-wider flex-shrink-0"
                                :class="product.badgeBarClass"
                            >
                                {{ getDurationLabel(product.durationKey) }}
                            </div>
                            <div class="flex-1 flex items-center justify-center gap-0.5 min-h-0" :class="product.badgeBarClass">
                                <SvgIcon name="route" class="text-white text-lg" />
                                <span class="text-white font-black text-base leading-none">{{ product.badgeLetter }}</span>
                            </div>
                        </div>

                        <h3 class="text-lg font-black text-gray-900 mb-2">{{ productCardTitle(product) }}</h3>
                        <p class="text-gray-800 text-sm mb-1 font-bold mb-4">{{ productPriceLabel(product) }}</p>
                        <p class="text-3xl font-black text-brand-blue mb-6">
                            {{ currencySymbol }} {{ formatPrice(product.price) }} *
                        </p>

                        <button
                            type="button"
                            class="mt-auto w-full font-bold py-3.5 rounded-lg text-white flex items-center justify-center gap-2 transition-all duration-300 group-hover:shadow-lg bg-buy-vignette hover:bg-buy-vignette-hover border-2 border-buy-vignette shadow-md text-[12px]"
                            @click="onSelectProduct(product)"
                        >
                            {{ getCtaText(product) }}
                            <SvgIcon name="arrow_forward" class="text-lg text-white transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        </div>
                    </div>
                </div>
            </div>
            <p v-else-if="!loading" class="text-center text-white/80 py-8">No products available.</p>
        </template>

        <!-- Default variant: fuel/eco tabs + product grid -->
        <template v-else>
            <div class="flex justify-center mb-8 px-4 sm:mb-12 sm:px-0">
                <div class="pricing-category-tabs flex flex-col w-full max-w-xl gap-1.5 bg-white rounded-xl p-1.5 shadow-sm border border-gray-200 overflow-hidden sm:inline-flex sm:flex-row sm:gap-1 sm:rounded-lg sm:p-1 sm:w-auto sm:max-w-none sm:overflow-visible">

                    <button
                        v-for="cat in categories"
                        :key="cat.id"
                        type="button"
                        class="pricing-category-tab box-border flex w-full min-w-0 items-center gap-3 px-4 py-3 rounded-lg text-sm font-bold transition-all text-left sm:w-auto sm:gap-2 sm:px-6 sm:py-2.5 sm:rounded-md sm:text-center"
                        :class="activeCategoryId === cat.id
                            ? 'bg-brand-blue text-white shadow-sm'
                            : 'text-gray-600 hover:text-gray-700 hover:bg-gray-50'"
                        @click="activeCategoryId = cat.id"
                    >
                        <img
                            :src="getVehicleCategoryIconPath(cat.id)"
                            alt=""
                            class="w-5 h-5 object-contain flex-shrink-0 pointer-events-none"
                            :class="activeCategoryId === cat.id ? 'brightness-0 invert' : ''"
                        />
                        <span class="leading-snug sm:leading-none flex-1 min-w-0">
                            {{ cat.title }}
                        </span>
                    </button>

                </div>
            </div>

            <div v-if="loading" class="flex justify-center items-center py-20">
                <div class="spinner"></div>
            </div>
            <div
                v-else
                class="grid gap-6 max-w-7xl mx-auto items-stretch"
                :class="pricingGridClass"
            >
                <template v-if="filteredProducts.length">
                    <div
                        v-for="(product, index) in filteredProducts"
                        :key="product.id"
                        class="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all flex flex-col relative"
                        :class="isMostPopularCard(product, index)
                            ? 'border-2 border-[#168941] z-10'
                            : 'border border-gray-100'"
                    >
                        <div class="p-6 border-b border-gray-50 flex-shrink-0 bg-gray-50/50 relative">
                            <svg
                                v-if="isMostPopularCard(product, index)"
                                xmlns="http://www.w3.org/2000/svg"
                                width="28"
                                height="28"
                                viewBox="0 -960 960 960"
                                class="absolute top-5 right-5 shrink-0 fill-slovenia-yellow"
                                aria-hidden="true"
                            >
                                <path d="m233-120 65-281L80-590l288-25 112-265 112 265 288 25-218 189 65 281-247-149-247 149Z" />
                            </svg>
                            <span
                                v-if="getCardMeta(product).pillLabel"
                                class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-bold uppercase mb-3"
                                :class="isMostPopularCard(product, index)
                                    ? 'bg-slovenia-yellow text-gray-900 text-[11px]'
                                    : 'bg-gray-700 text-white text-[10px]'"
                            >
                                <span class="truncate max-w-[200px]">
                                    {{ texts[getCardMeta(product).pillLabel] || getCardMeta(product).pillLabel }}
                                </span>
                            </span>
                            <h3 class="text-xl font-black text-gray-900">{{ productCardTitle(product) }}</h3>
                            <p class="text-sm text-gray-500 mt-1">
                                {{ texts[getCardMeta(product).subtitleKey] || getCardMeta(product).subtitle }}
                            </p>
                        </div>
                        <div class="p-6 flex-1 flex flex-col">
                            <div class="mb-6">
                                <span class="text-3xl font-black text-brand-blue">{{ currencySymbol }}{{ formatPrice(product.price) }}</span>
                                <span class="text-gray-400 text-sm font-medium">{{ texts['home-section-4.blade.php-4-card-2-per'] }}</span>
                            </div>
                            <ul class="space-y-3 mb-8 flex-1 ps-0">
                                <li
                                    v-for="(key, fi) in getCardMeta(product).featureKeys"
                                    :key="fi"
                                    class="flex items-start gap-2 text-sm text-gray-600"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 -960 960 960" fill="rgb(22,163,74)" class="shrink-0 mt-0.5"><path d="M378-246 154-470l43-43 181 181 384-384 43 43-427 427Z"/></svg>
                                    <span>{{ texts[key] || getCardMeta(product).features[fi] }}</span>
                                </li>
                            </ul>
                            <a
                                :href="product.categoryPath || ''"
                                class="w-full font-bold py-3.5 rounded-lg transition-colors flex items-center justify-center gap-2 group no-underline !text-[12px] shadow-md"
                                :class="isMostPopularCard(product, index)
                                    ? 'bg-buy-vignette hover:!bg-buy-vignette-hover !text-white'
                                    : 'bg-white !text-gray-900 border-2 border-buy-vignette hover:!bg-buy-vignette hover:!text-white hover:!border-buy-vignette'"
                                @click="onSelectProduct(product, $event)"
                            >
                                {{ getCtaText(product) }}
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 -960 960 960" fill="currentColor" class="shrink-0 transition-transform group-hover:translate-x-1"><path d="M686-450H160v-60h526L438-758l42-42 320 320-320 320-42-42 248-248Z"/></svg>
                            </a>
                        </div>
                    </div>
                </template>
                <p v-else class="col-span-full text-center text-gray-500 py-8">No products for this category.</p>
            </div>
        </template>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { getVehicleCategoryIconPath } from '../utils/vehicleCategoryIcons.js';
import { getProductTotalWithFees } from '../utils/productPricing.js';
import {
    getProductDurationDays,
    getProductDurationKey,
    getProductDurationBucket,
    formatProductDurationLabel,
    compareProductDurations,
} from '../utils/productDuration.js';
import { parseCategoryDescriptionPayload, parseProductDescriptionPayload } from '../utils/categoryDescription.js';

defineOptions({ name: 'PricingSection' });

const props = defineProps({
    variant: { type: String, default: null },
    forceEuro: { type: Boolean, default: false },
    ctaTextKey: { type: String, default: null },
    sectionObjects: { type: Object, default: () => ({}) },
    sectionTexts: { type: Object, default: () => ({}) },
    standardPriceFromBlade: { type: String, default: '' }
});

const store = useStore();
const activeCategoryId = ref(null);
const loading = ref(false);
const error = ref(null);

const hasPrefetchedCatalog = () =>
    (store.getters['form/categorizedProducts'] || []).length > 0;

if (props.variant === 'hero' && hasPrefetchedCatalog()) {
    activeCategoryId.value = store.getters['form/categorizedProducts'][0]?.id ?? null;
}

const texts = computed(() => store.getters.texts || {});
const currentLang = computed(() => store.getters.lang || null);

const storeCurrency = computed(() => store.getters['currency/currentCurrency'] || {});
const currencies = computed(() => store.getters.currencies || []);

function resolveCurrencyByCode(code) {
    const normalized = String(code ?? '').trim().toUpperCase();
    const match = currencies.value.find(
        (item) => String(item?.code ?? '').trim().toUpperCase() === normalized
    );

    if (match) {
        return {
            ...match,
            code: normalized,
            symbol: String(match?.symbol ?? '€').trim(),
            conversion: Number(match?.conversion ?? match?.rate ?? 1) || 1,
        };
    }

    if (normalized === 'EUR') {
        return { code: 'EUR', symbol: '€', conversion: 1 };
    }

    return null;
}

/** Second home always shows EUR; other pages follow the active store currency. */
const displayCurrency = computed(() => {
    if (props.forceEuro) {
        return resolveCurrencyByCode('EUR') || { code: 'EUR', symbol: '€', conversion: 1 };
    }

    return storeCurrency.value;
});

const currencySymbol = computed(() =>
    displayCurrency.value?.symbol ||
    currencies.value?.[0]?.symbol ||
    '€'
);
const categories = computed(() =>
    categorizedProducts.value.map((c) => ({
        id: c.id,
        title: c.translated?.title || 'Category',
        description: c.translated?.description || c.description || ''
    }))
);

/** Hero (second home): tab title + subtitle from JSON in category description. */
const heroCategories = computed(() =>
    categorizedProducts.value
        .filter((c) => c?.products?.length)
        .map((c) => {
            const meta = parseCategoryDescriptionPayload(c);
            return {
                id: c.id,
                title: meta.title,
                text: meta.text,
            };
        })
);

/** Subtitle under hero category tabs — JSON `text` key. */
const activeCategoryText = computed(() => {
    if (props.variant !== 'hero') return '';
    const cat = heroCategories.value.find((c) => c.id === activeCategoryId.value);
    return cat?.text ? String(cat.text).trim() : '';
});
function convertPrice(value) {
    const n = Number(value || 0);
    return n * currencyRate.value;
}

const currencyRate = computed(() =>
    Number(displayCurrency.value?.conversion ?? 1) || 1
);

/**
 * Map canonical "duration in days" buckets to text keys used as pill labels.
 * Keys deliberately use days (not raw API duration) so 1-month → 30,
 * 6-month → 180, 1-year → 365 all hit the same bucket regardless of how the
 * backend encodes them.
 */
const DURATION_LABEL_MAP = {
    '1': 'duration-transit',
    '7': 'duration-most_popular',
    '10': 'duration-most_popular',
    '30': 'duration-extended',
    '180': 'duration-extended',
    '365': 'duration-annual_pass'
};

function getDurationLabel(label) {
    const key = DURATION_LABEL_MAP[label];
    return texts.value[key] || label;
}

// durationKey = canonical DAYS (e.g. '7', '30', '180', '365')
// UI label must always use getDurationLabel(durationKey)

/** standard-price: Blade attr first ($__texts['standard-price']), then sectionTexts / store, then fallback */
const standardPriceLabel = computed(() => {
    if (props.standardPriceFromBlade && props.standardPriceFromBlade.trim()) return props.standardPriceFromBlade;
    return texts.value['standard-price']
        || texts.value['standard_price']
        || 'Standard price';
});
// const currencySymbol = computed(() => {
//     const c = store.getters['currency/currentCurrency'] || store.getters.currencies?.[0];
//     return c?.symbol ?? '€';
// });

const rawProducts = computed(() => store.getters['form/products'] || []);
const categorizedProducts = computed(() => store.getters['form/categorizedProducts'] || []);
const productIconItems = computed(() => store.getters['form/productIconItems'] || []);

function durationKeyFromCategoryRow(row) {
    /**
     * Use the marketing bucket from the first product. Buckets absorb
     * off-by-one backend conventions (e.g. duration=6 → "7-day bucket"),
     * which is exactly the lookup CARD_META expects.
     */
    const firstProduct = row?.products?.[0];
    return getProductDurationBucket(firstProduct);
}

function durationOrderFromKey(k) {
    const order = { 1: 1, 7: 2, 10: 3, 30: 4, 180: 5, 365: 6 };
    const n = Number(k);
    return order[n] ?? n ?? 999;
}

/**
 * Single-letter badge for the duration card: Y for ≥ year, M for monthly
 * buckets (30/180), D for everything else. Driven by canonical days so it
 * works no matter how the API encodes the period.
 */
function badgeLetterForKey(durationKey) {
    const n = Number(durationKey);
    if (!Number.isFinite(n) || n <= 0) return 'D';
    if (n >= 365) return 'Y';
    if (n >= 30) return 'M';
    return 'D';
}
function productTitleLower(p) {
    return String(p?.translated?.title ?? p?.title ?? p?.name ?? '').toLowerCase();
}

/**
 * Pricing card product per category bucket: match CMS `page_id` (not product id).
 * fuel tab = petrol/diesel; eco tab = CNG/biomethane.
 */

function categoryProductsEnabled(row) {
    const list = Array.isArray(row?.products) ? row.products : [];
    return list.filter((p) => p && p.enabled !== false);
}

/** Fallback if page_id map misses (wrong locale / API shape). */
function pickCategoryProductForTab(products, tab) {
    const list = Array.isArray(products) ? products : [];
    if (!list.length) return null;
    if (tab === 'fuel') {
        const petrol = list.find((p) => {
            const t = productTitleLower(p);
            return (
                t.includes('petrol')
                || t.includes('diesel')
                || t.includes('benzine')
                || t.includes('benzin')
                || t.includes('gasoline')
                || t.includes('essence')
            );
        });
        if (petrol) return petrol;
    } else {
        const cng = list.find((p) => {
            const t = productTitleLower(p);
            return t.includes('cng') || t.includes('biomethane');
        });
        if (cng) return cng;
        const electric = list.find((p) => {
            const t = productTitleLower(p);
            return t.includes('electric') || t.includes('elektrisch');
        });
        if (electric) return electric;
        const plugin = list.find((p) => /hybrid/.test(productTitleLower(p)) && /plug|plugin/.test(productTitleLower(p)));
        if (plugin) return plugin;
    }
    return list[0];
}

function pickCategoryPricingProduct(row, tab) {
    const list = categoryProductsEnabled(row);
    if (!list.length) return null;
    const durationKey = durationKeyFromCategoryRow(row);
    if (!durationKey) return null;

    return pickCategoryProductForTab(list, tab);
}

/** Card meta for default variant (Slovenian Vignette Prices page): pill, subtitle, feature text keys + fallbacks */
const CARD_META = {
    '7': {
        pillLabel: 'duration-most_popular',
        pillClass: 'bg-slovenia-yellow text-gray-900',
        subtitleKey: 'home-section-4.blade.php-4-card-1-desc',
        subtitle: 'Perfect for quick transit',
        featureKeys: ['home-section-4.blade.php-4-card-1-item-1', 'home-section-4.blade.php-4-card-1-item-2', 'home-section-4.blade.php-4-card-1-item-3'],
        features: ['Valid for 24 hours', 'Instant activation', 'No adhesive sticker']
    },
    '180': {
        pillLabel: 'duration-extended',
        pillClass: 'bg-blue-100 text-brand-blue border border-blue-200',
        subtitleKey: 'home-section-4.blade.php-4-card-2-desc',
        subtitle: 'Perfect for holidays & weekends',
        featureKeys: ['home-section-4.blade.php-4-card-2-item-1', 'home-section-4.blade.php-4-card-2-item-2', 'home-section-4.blade.php-4-card-2-item-3'],
        features: ['Valid immediately', 'Digital delivery', 'No adhesive sticker']
    },
    '30': {
        pillLabel: 'duration-extended',
        pillClass: 'bg-blue-100 text-brand-blue border border-blue-200',
        subtitleKey: 'home-section-4.blade.php-4-card-3-desc',
        subtitle: 'For longer stays & monthly trips',
        featureKeys: ['home-section-4.blade.php-4-card-3-item-1', 'home-section-4.blade.php-4-card-3-item-2', 'home-section-4.blade.php-4-card-3-item-3'],
        features: ['Best value for vacations', 'Valid for 30 consecutive days', 'Digital delivery']
    },
    '365': {
        pillLabel: 'duration-annual_pass',
        pillClass: 'bg-blue-100 text-brand-blue border border-blue-200',
        subtitleKey: 'home-section-4.blade.php-4-card-4-desc',
        subtitle: 'Frequent travelers & commuters',
        featureKeys: ['home-section-4.blade.php-4-card-4-item-1', 'home-section-4.blade.php-4-card-4-item-2', 'home-section-4.blade.php-4-card-4-item-3'],
        features: ['Valid until Jan 31st next year', 'Maximum flexibility', 'Digital delivery']
    }
};

/**
 * Maps product title (any locale) to vignette duration bucket + sort order.
 * Order: 1 → 10 → 30/month → 365. Includes DE (Tag/Tage/Jahr/Monat), NL, EN.
 */
function detectDurationBadgeFromTitle(rawTitle) {
    const t = String(rawTitle || '').toLowerCase();

    if (/\b7\s*(?:day[s]?|dagen?|tage|tag)\b/i.test(t)) {
        return { order: 1, badgeLabel: '7' };
    }

    if (/\b10\s*(?:day[s]?|dagen?|dag|tage)\b/i.test(t)) {
        return { order: 2, badgeLabel: '10' };
    }

    if (
        /\b30\s*(?:day[s]?|dagen?|dag|daagse|tage)\b/i.test(t) ||
        /\b1\s*(?:month|maand|monat)\b/i.test(t)
    ) {
        return { order: 3, badgeLabel: '30' };
    }

    if (
        /\b365\s*(?:day[s]?|dagen?|tage)\b/i.test(t) ||
        /\b1\s*(?:year|jaar|jahr)\b/i.test(t)
    ) {
        return { order: 4, badgeLabel: '365' };
    }

    if (/\b1\s*(?:day|dag|tag)\b/i.test(t)) {
        return { order: 1, badgeLabel: '1' };
    }

    return null;
}

function getBadgeLabelFromTitle(title) {
    return detectDurationBadgeFromTitle(title)?.badgeLabel ?? '';
}

/**
 * Single source of duration bucket: API fields > title heuristics. Returns
 * the marketing bucket ('1' | '7' | '10' | '30' | '180' | '365') so it
 * matches CARD_META keys regardless of how the backend encodes the raw
 * duration (`6 days` → `'7'`, `1 month` → `'30'`, `6 months` → `'180'`).
 */
function resolveProductDurationKey(product) {
    const apiBucket = getProductDurationBucket(product);
    if (apiBucket) return apiBucket;
    const title = product?.translated?.title ?? product?.title ?? '';
    return getBadgeLabelFromTitle(title);
}

function getCardMeta(product) {
    const badgeLabel = product?.durationKey ?? product?.badgeLabel ?? getBadgeLabelFromTitle(product?.title);
    return (badgeLabel && CARD_META[badgeLabel]) || { pillKey: '', pillLabel: '', pillClass: '', subtitleKey: '', subtitle: '', featureKeys: [], features: [] };
}

/** Only the first card in the active category grid gets popular styling. */
function isMostPopularCard(product, index) {
    return !!product && index === 0;
}

function resolveTextKey(key) {
    if (!key) return '';
    const fromStore = texts.value[key];
    if (fromStore && String(fromStore).trim()) return fromStore;
    const fromSection = props.sectionTexts?.[key];
    if (fromSection && String(fromSection).trim()) return fromSection;
    return '';
}

/**
 * CTA button text: optional page-level override (e.g. home-second → purchase-now),
 * then API product title, then legacy duration text keys.
 */
function getCtaText(product) {
    if (props.ctaTextKey) {
        const override = resolveTextKey(props.ctaTextKey);
        if (override) return override;
    }

    const fromProduct = firstNonEmpty(
        normalizeShortTitleValue(product?.btnTitle),
        normalizeShortTitleValue(product?.title),
        normalizeShortTitleValue(product?.shortTitle)
    );
    if (fromProduct) return fromProduct;

    const days = Number(product?.durationKey);
    const candidates = [];
    if (days === 365) candidates.push('1-year');
    else if (days === 180) candidates.push('6-month');
    else if (days === 30) candidates.push('1-month', '30-day');
    else if (days === 10) candidates.push('10-day');
    else if (days === 7) candidates.push('7-day');
    else if (days === 1) candidates.push('1-day');

    for (const key of candidates) {
        const t = texts.value[key];
        if (t && String(t).trim()) return t;
    }
    return product?.durationLabel || '';
}
function getPillLabel(meta) {
    return texts.value[meta?.pillLabel] || meta?.pillLabel || '';
}
/**
 * Build thumbnail map from section objects, keyed by canonical days.
 * Backend may expose either day-keyed slots (`product_thumbnail_30_day`)
 * or unit-keyed ones (`product_thumbnail_1_month`); we try both so a
 * single Blade attribute lookup covers the new and legacy schemas.
 */
const productThumbnails = computed(() => {
    const objs = props.sectionObjects || {};
    const src = (key) => {
        const o = objs[key];
        if (!o) return null;
        return o.thumbnail?.src ?? o.image?.src ?? o.thumbnail ?? o.image ?? null;
    };
    return {
        '1':   src('product_thumbnail_1_day'),
        '7':   src('product_thumbnail_7_day') || src('product_thumbnail_7_days'),
        '10':  src('product_thumbnail_10_day') || src('product_thumbnail_10_days'),
        '30':  src('product_thumbnail_30_day') || src('product_thumbnail_1_month'),
        '180': src('product_thumbnail_180_day') || src('product_thumbnail_6_month') || src('product_thumbnail_6_months'),
        '365': src('product_thumbnail_365_day') || src('product_thumbnail_1_year'),
    };
});

/**
 * Hero (legacy fallback when /products/categorized is empty): up to 4 cards,
 * one per unique duration bucket, sorted shortest → longest.
 *
 * Earlier this section split products by fuel/eco keywords in the title —
 * irrelevant for Slovenia's vehicle-class vignettes, so we drop that filter
 * and dedup purely on duration.
 */
const heroProducts = computed(() => {
    const styles = [
        { border: 'border-brand-blue', bar: 'bg-brand-blue' },
        { border: 'border-red-600', bar: 'bg-red-600' },
        { border: 'border-amber-500', bar: 'bg-amber-500' },
        { border: 'border-green-600', bar: 'bg-green-600' }
    ];
    const list = rawProducts.value
        .map((p) => {
            const fullTitle = p.translated?.title ?? p.title ?? p.name ?? '';
            const fullDescription = p.translated?.description ?? p.description ?? '';
            const durationKey = resolveProductDurationKey(p);
            if (!durationKey) return null;
            const order = durationOrderFromKey(durationKey);
            const thumbnailUrl = p.thumbnail?.src ?? p.thumbnail ?? p.image?.src ?? p.image ?? null;
            return {
                order,
                id: p.id,
                title: fullTitle,
                btnTitle: p.translated?.btn_title ?? p.btn_title ?? p.btnTitle ?? '',
                description: fullDescription,
                /** Bake enabled `fee` extensions into displayed price (see productPricing.js). */
                price: getProductTotalWithFees(p),
                durationLabel: formatProductDurationLabel(p),
                badgeLabel: durationKey,
                durationKey,
                badgeLetter: badgeLetterForKey(durationKey),
                vignetteTitle: fullTitle,
                shortTitle: extractShortTitle(p),
                thumbnailUrl,
                /** Single category — keeps the rest of the code path uniform. */
                category: 'fuel',
            };
        })
        .filter(Boolean);
    list.sort((a, b) => a.order - b.order);
    const seen = new Set();
    const out = [];
    for (const p of list) {
        if (seen.has(p.durationKey)) continue;
        seen.add(p.durationKey);
        const s = styles[out.length] || styles[0];
        out.push({ ...p, badgeBorderClass: s.border, badgeBarClass: s.bar });
        if (out.length >= 4) break;
    }
    return out;
});

/** Hero cards from GET /products/categorized (category title + representative fuel price). */
const heroFromCategories = computed(() => {
    const styles = [
        { border: 'border-brand-blue', bar: 'bg-brand-blue' },
        { border: 'border-red-600', bar: 'bg-red-600' },
        { border: 'border-amber-500', bar: 'bg-amber-500' },
        { border: 'border-green-600', bar: 'bg-green-600' }
    ];
    const rows = categorizedProducts.value.filter((r) => r?.products?.length);
    const items = rows
        .map((row) => {
            const p = pickCategoryPricingProduct(row, 'fuel');
            if (!p) return null;
            const durationKey = durationKeyFromCategoryRow(row);
            if (!durationKey) return null;
            const order = durationOrderFromKey(durationKey);
            const badgeLetter = badgeLetterForKey(durationKey);
            const catTitle = String(row.translated?.title ?? '').trim();
            const thumb =
                row.thumbnail?.src
                ?? row.thumbnail?.url
                ?? p.thumbnail?.src
                ?? p.thumbnail
                ?? null;
            const pathRaw = row.path;
            const categoryPath = typeof pathRaw === 'string' ? pathRaw.trim() : '';
            return {
                order,
                id: p.id,
                title: catTitle,
                description: row.translated?.description ?? '',
                /** Bake enabled `fee` extensions into displayed price (see productPricing.js). */
                price: getProductTotalWithFees(p),
                /** Pre-formatted duration text for hero card heading + CTA fallback. */
                durationLabel: formatProductDurationLabel(p),
                badgeLabel: durationKey,
                durationKey,
                badgeLetter,
                vignetteTitle: catTitle,
                shortTitle: catTitle,
                thumbnailUrl: thumb,
                category: 'fuel',
                categoryPath,
                categoryId: row.id
            };
        })
        .filter(Boolean);
    items.sort((a, b) => a.order - b.order);
    const out = [];
    for (let i = 0; i < items.length; i++) {
        const p = items[i];
        const s = styles[out.length] || styles[0];
        out.push({ ...p, badgeBorderClass: s.border, badgeBarClass: s.bar });
        if (out.length >= 4) break;
    }
    return out;
});

const HERO_BADGE_STYLES = [
    { border: 'border-brand-blue', bar: 'bg-brand-blue' },
    { border: 'border-red-600', bar: 'bg-red-600' },
    { border: 'border-amber-500', bar: 'bg-amber-500' },
    { border: 'border-green-600', bar: 'bg-green-600' },
];

/** Hero purchase cards: one product per duration for the active vehicle category. */
const heroCategoryProducts = computed(() => {
    if (props.variant !== 'hero') return [];

    if (categorizedProducts.value.length) {
        const row = categorizedProducts.value.find((r) => r.id === activeCategoryId.value);
        if (!row) return [];

        return (row.products || [])
            .filter((p) => p && p.enabled !== false)
            .slice()
            .sort(compareProductDurations)
            .map((p, index) => {
                const durationKey = getProductDurationBucket(p);
                const badgeStyle = HERO_BADGE_STYLES[index] || HERO_BADGE_STYLES[0];
                const descMeta = parseProductDescriptionPayload(p);
                return {
                    id: p.id,
                    title: p.translated?.title ?? p.title ?? p.name ?? '',
                    descriptionJsonTitle: descMeta.hasJsonTitle ? descMeta.title : '',
                    descriptionJsonText: descMeta.hasJsonTitle ? descMeta.text : '',
                    btnTitle: p.translated?.btn_title ?? p.btn_title ?? p.btnTitle ?? '',
                    description: p.translated?.description ?? p.description ?? '',
                    shortTitle: extractShortTitle(p),
                    price: getProductTotalWithFees(p),
                    durationLabel: formatProductDurationLabel(p),
                    durationKey,
                    badgeLetter: badgeLetterForKey(durationKey),
                    badgeBorderClass: badgeStyle.border,
                    badgeBarClass: badgeStyle.bar,
                    categoryPath: p.path ?? row.path ?? '',
                    categoryId: row.id,
                    thumbnailUrl:
                        row.thumbnail?.src
                        ?? row.thumbnail?.url
                        ?? p.thumbnail?.src
                        ?? p.thumbnail
                        ?? null,
                };
            });
    }

    return heroProducts.value.filter((p) => p.category === 'fuel');
});

/** Match hero grid columns to card count (up to 4). */
const heroPricingGridClass = computed(() => {
    const n = heroCategoryProducts.value.length;
    if (n <= 1) return 'grid-cols-1';
    if (n === 2) return 'grid-cols-1 sm:grid-cols-2';
    if (n === 3) return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
    return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
});

/** Hero variant: products filtered by active tab (fuel / eco) — legacy fallback */
const heroFilteredProducts = computed(() => {
    if (props.variant === 'hero' && categorizedProducts.value.length) {
        return heroFromCategories.value;
    }
    const list = heroProducts.value;
    return props.variant === 'hero' ? list.filter((p) => p.category === 'fuel') : list.filter((p) => p.category === activeTab.value);
});

function firstNonEmpty(...values) {
    for (const v of values) {
        if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
}

function normalizeShortTitleValue(value) {
    if (value == null) return '';
    const raw = String(value);
    // CMS editor may return HTML (<p>1 aaa</p>); keep plain text for card heading.
    const plain = raw
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return plain;
}

function readShortTitleFromObject(obj) {
    if (!obj || typeof obj !== 'object') return '';
    return normalizeShortTitleValue(firstNonEmpty(
        obj.short_title,
        obj['short-title'],
        obj.shortTitle,
        obj.title_short,
        obj['title-short'],
        obj.titleShort,
        obj['short title']
    ));
}

function readShortTitleFromTranslations(translations) {
    if (!Array.isArray(translations) || !translations.length) return '';

    const langId = String(currentLang.value?.id ?? '');
    const langCode = String(currentLang.value?.code ?? '').toLowerCase().trim();

    const pick = translations.find((tr) => {
        const trId = String(tr?.language_id ?? tr?.lang_id ?? tr?.languageId ?? '');
        const trCode = String(tr?.code ?? tr?.lang_code ?? tr?.language?.code ?? '')
            .toLowerCase()
            .trim();

        if (langId && trId && trId === langId) return true;
        if (langCode && trCode && trCode === langCode) return true;
        return false;
    });

    return (
        readShortTitleFromObject(pick) ||
        translations.map((tr) => readShortTitleFromObject(tr)).find(Boolean) ||
        ''
    );
}

function readShortTitleFromItem(item) {
    if (!item || typeof item !== 'object') return '';
    const key = String(item.key ?? item.name ?? '').toLowerCase().trim();
    if (!['short-title', 'short_title', 'title_short'].includes(key)) return '';

    const langId = String(currentLang.value?.id ?? '');
    const tr = Array.isArray(item.translations)
        ? item.translations.find((t) => String(t?.language_id ?? t?.lang_id ?? '') === langId)
        : null;

    return normalizeShortTitleValue(firstNonEmpty(
        tr?.value,
        tr?.title,
        item.value,
        item.content,
        item.text,
        item.html
    ));
}

function readShortTitleFromProductItems(product) {
    const pid = Number(product?.id);
    if (!pid) return '';
    const rows = productIconItems.value || [];
    for (const row of rows) {
        const rowPid = Number(row?.product_id);
        const rowPids = Array.isArray(row?.product_ids) ? row.product_ids.map((x) => Number(x)) : [];
        const match = rowPid === pid || rowPids.includes(pid);
        if (!match) continue;
        const short = readShortTitleFromItem(row);
        if (short) return short;
    }
    return '';
}

function extractShortTitle(product) {
    if (!product || typeof product !== 'object') return '';
    return firstNonEmpty(
        readShortTitleFromObject(product),
        readShortTitleFromObject(product.translated),
        readShortTitleFromTranslations(product.translations),
        readShortTitleFromObject(product.category),
        readShortTitleFromObject(product.category?.translated),
        readShortTitleFromTranslations(product.category?.translations),
        readShortTitleFromProductItems(product)
    );
}

function cardHeading(product) {
    return firstNonEmpty(
        normalizeShortTitleValue(product?.description),
        normalizeShortTitleValue(product?.title),
        normalizeShortTitleValue(product?.vignetteTitle)
    );
}

/** Card heading: hero uses JSON `title` from product description; default uses API title. */
function productCardTitle(product) {
    if (props.variant === 'hero') {
        const fromJson = normalizeShortTitleValue(product?.descriptionJsonTitle);
        if (fromJson) return fromJson;
    }

    return firstNonEmpty(
        normalizeShortTitleValue(product?.shortTitle),
        normalizeShortTitleValue(product?.title),
        normalizeShortTitleValue(product?.durationLabel),
        cardHeading(product)
    );
}

/** Hero price line: JSON `text` per product, else translated "Standard price" label. */
function productPriceLabel(product) {
    const fromJson = String(product?.descriptionJsonText ?? '').trim();
    if (fromJson) return fromJson;
    return `${standardPriceLabel.value} :`;
}

function isHeroMostPopularCard(product, index) {
    if (!product) return false;
    const meta = getCardMeta(product);
    if (meta?.pillLabel === 'duration-most_popular') return true;
    return index === 0;
}
const normalizedProducts = computed(() => {
    const list = rawProducts.value
        .map((p) => {
            const title = p.translated?.title ?? p.title ?? '';
            const lowerTitle = title.toLowerCase();

            let category = null;

            if (!category) return null;

            const durationKey = resolveProductDurationKey(p);
            const durationOrder = durationOrderFromKey(durationKey);
            const badgeLabel = durationKey;
            const meta = badgeLabel ? CARD_META[badgeLabel] : null;

            return {
                id: p.id,
                title,
                description: p.translated?.description ?? p.description ?? '',
                shortTitle: extractShortTitle(p),
                /** Bake enabled `fee` extensions into displayed price (see productPricing.js). */
                price: getProductTotalWithFees(p),
                period: p.period ?? '',
                badge: p.badge ?? '',
                badge_type: p.badge_type ?? 'blue',
                category,
                features: meta?.features ?? [],
                is_popular: !!p.is_popular,
                badgeLabel,
                durationKey,
                durationOrder
            };
        })
        .filter(Boolean);
    list.sort((a, b) => {
        if (a.durationOrder !== b.durationOrder) return a.durationOrder - b.durationOrder;
        return (Number(a.id) || 0) - (Number(b.id) || 0);
    });
    return list;
});

/**
 * Default pricing grid: one card per duration in the currently active category.
 *
 * Cards arrive sorted shortest → longest (canonical days). The weekly /
 * short-trip card (`duration-most_popular` bucket) gets `is_popular` styling.
 */
const gridProductsFromCategories = computed(() => {
    if (!categorizedProducts.value.length) return [];

    const row = categorizedProducts.value.find(
        (r) => r.id === activeCategoryId.value
    );

    if (!row) return [];

    const products = (row.products || [])
        .filter((p) => p.enabled)
        .slice()
        .sort(compareProductDurations);

    return products.map((p, index) => ({
        id: p.id,
        title: p.translated?.title ?? p.title ?? p.name ?? '',
        btnTitle: p.translated?.btn_title ?? p.btn_title ?? p.btnTitle ?? '',
        shortTitle: extractShortTitle(p),
        /** Clean duration text for CTA fallback: "7 days", "1 month", "1 year". */
        durationLabel: formatProductDurationLabel(p) || (p.duration ?? ''),
        /** Bake enabled `fee` extensions into displayed price (see productPricing.js). */
        price: getProductTotalWithFees(p),
        /**
         * Marketing bucket so CARD_META[durationKey] always resolves —
         * absorbs off-by-one backend conventions (e.g. duration=6 vs label "7 days").
         */
        durationKey: getProductDurationBucket(p),
        categoryPath: p.path,
        categoryId: row.id,
        is_popular: index === 0,
    }));
});

const filteredProducts = computed(() => {
    if (categorizedProducts.value.length) {
        return gridProductsFromCategories.value;
    }
    return normalizedProducts.value.filter((p) => p.category === activeTab.value);
});

/** Match grid columns to card count so a short row (e.g. 3 cards) stays centered, not left in a 4-col grid. */
const pricingGridClass = computed(() => {
    const n = filteredProducts.value.length;
    if (n <= 1) return 'grid-cols-1';
    if (n === 2) return 'grid-cols-1 md:grid-cols-2';
    if (n === 3) return 'grid-cols-1 md:grid-cols-3';
    return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';
});

function badgeClass(badgeType) {
    const t = (badgeType || 'blue').toLowerCase();
    if (t === 'red') return 'bg-red-100 text-primary';
    if (t === 'gray') return 'bg-gray-100 text-gray-600';
    return 'bg-blue-100 text-brand-blue';
}

function formatPrice(price) {
    const converted = convertPrice(price);

    return new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(converted);
}

function getProductThumbnail(product) {
    if (!product) return null;
    const objs = props.sectionObjects || {};
    const byId = (id) => {
        const o = objs['product_thumbnail_' + id];
        return o?.thumbnail?.src ?? o?.image?.src ?? o?.thumbnail ?? o?.image ?? null;
    };
    if (product.id && byId(product.id)) return byId(product.id);
    if (product.thumbnailUrl) return product.thumbnailUrl;
    return productThumbnails.value[product.durationKey] || null;
}

function heroSelectLabel(product) {
    if (!product?.durationKey) return '';
    return getDurationLabel(product.durationKey);
}

function onSelectProduct(product, ev) {
    if (!product?.id) return;
    if (ev && typeof ev.preventDefault === 'function') {
        ev.preventDefault();
    }

    const patch = { product_id: product.id };
    if (product.categoryId != null && product.categoryId !== '') {
        patch.category_id = product.categoryId;
    }
    store.commit('form/SET_FORM_DATA', patch);
    store.dispatch('form/saveDataToLocalStorage');

    const dest =
        (product.categoryPath && String(product.categoryPath).trim()) || '/buy-vignette';
    window.location.assign(dest);
}

onMounted(async () => {
    if (props.variant === 'hero') {
        if (!hasPrefetchedCatalog()) {
            loading.value = true;
        }

        try {
            await store.dispatch('form/loadProductCatalog');

            if (categorizedProducts.value.length && activeCategoryId.value == null) {
                activeCategoryId.value = categorizedProducts.value[0].id;
            }
        } catch (e) {
            error.value = e?.message;
        } finally {
            loading.value = false;
        }

        return;
    }

    loading.value = true;
    try {
        await store.dispatch('form/loadInitialData');

        if (categorizedProducts.value.length) {
            activeCategoryId.value = categorizedProducts.value[0].id;
        }
    } catch (e) {
        error.value = e?.message;
    } finally {
        loading.value = false;
    }
});
</script>

<style scoped>
.spinner {
    border: 4px solid rgba(10, 69, 143, 0.2);
    border-top-color: #11457E;
    border-radius: 50%;
    width: 48px;
    height: 48px;
    animation: spin 0.8s linear infinite;
}
@keyframes spin {
    to { transform: rotate(360deg); }
}
.product-header {
    height: 140px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start
}
</style>
